import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "@/context/LanguageContext";
import { MODELS, type AIModel, type Plan } from "@/lib/ai/models";

interface ModelSelectorProps {
  selectedModel?: string;
  onModelChange?: (model: string) => void;
  userPlan?: Plan;
  onUpgradeRequest?: () => void;
}

export const ModelSelector = ({ 
  selectedModel = "auto", 
  onModelChange,
  userPlan = "free",
  onUpgradeRequest,
}: ModelSelectorProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [internalModel, setInternalModel] = useState(selectedModel);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const currentModelValue = onModelChange ? selectedModel : internalModel;
  const currentModel = MODELS.find(m => m.value === currentModelValue) || MODELS[0];

  const isLocked = (model: AIModel) =>
    model.tier === "pro" && userPlan !== "pro";

  // Calculate position for portal
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const updatePosition = () => {
        if (containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();
          setPosition({
            top: rect.top,
            left: rect.left,
          });
        }
      };
      updatePosition();
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [isOpen]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleModelChange = (model: AIModel) => {
    if (isLocked(model)) {
      onUpgradeRequest?.();
      setIsOpen(false);
      return;
    }

    if (onModelChange) {
      onModelChange(model.value);
    } else {
      setInternalModel(model.value);
    }
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        role="combobox"
        aria-expanded={isOpen}
        aria-autocomplete="none"
        data-state={isOpen ? "open" : "closed"}
        onClick={() => setIsOpen(!isOpen)}
        className="mb-1 flex h-7 w-fit items-center justify-between rounded-full bg-transparent p-1.5 text-sm text-primary/60 transition-all hover:bg-primary/5"
      >
        <span className="mr-1 flex items-center pl-0.5 text-xs">
          <Sparkles className="block h-4 w-4 flex-shrink-0 md:hidden" />
          <span className="ml-1 hidden capitalize md:block">{currentModel.labelKey ? t(currentModel.labelKey) : currentModel.label}</span>
        </span>
        <ChevronDown className={`h-4 w-4 opacity-50 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <>
          {createPortal(
            <div 
              className="fixed inset-0 z-[99]"
              onClick={() => setIsOpen(false)}
            />,
            document.body
          )}
          {createPortal(
            <div 
              ref={dropdownRef}
              className="fixed z-[100] bg-popover border border-border rounded-xl shadow-lg py-1.5 w-max min-w-[180px]"
              style={{
                top: `${position.top}px`,
                left: `${position.left}px`,
                transform: "translateY(calc(-100% - 8px))",
              }}
            >
              {MODELS.filter(m => !m.hidden).map((model) => (
                <button
                  key={model.value}
                  type="button"
                  onClick={() => handleModelChange(model)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors flex items-center justify-between gap-3 whitespace-nowrap ${
                    currentModelValue === model.value ? "text-primary font-medium" : "text-popover-foreground"
                  }`}
                >
                  <span className="whitespace-nowrap">{model.labelKey ? t(model.labelKey) : model.label}</span>
                  {isLocked(model) && (
                    <Badge className="text-xs px-1.5 py-0 h-5 text-success bg-success/10 border-success/20 pointer-events-none">
                      {t("settings.upgrade")}
                    </Badge>
                  )}
                </button>
              ))}
            </div>,
            document.body
          )}
        </>
      )}
    </div>
  );
};
